import AlertBanner from 'ext-ui/components/AlertBanner/AlertBanner'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import { yellow800 } from 'ext-ui/style/colors'
import AlertTriangleIcon from 'ext-ui/svg/icon/AlertTriangleIcon'
import moment from 'moment'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import ManageSubscriptionHeader from 'src/app/ManageSubscription/components/ManageSubscriptionHeader/ManageSubscriptionHeader'
import ManageSubscriptionPromo from 'src/app/ManageSubscription/components/ManageSubscriptionPromo/ManageSubscriptionPromo'
import ManageSubscriptionUsageMeters from 'src/app/ManageSubscription/components/ManageSubscriptionUsageMeters/ManageSubscriptionUsageMeters'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import ChurnSurveyScreen from 'src/component-modal/ChurnSurveyModal/ChurnSurveyModal'
import { useChurnSurveySubmit } from 'src/component-modal/ChurnSurveyModal/useChurnSurveySubmit'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPage from 'src/components/NotFoundBackgroundPage/NotFoundBackgroundPage'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import {
  getSaasTierLabel,
  SubscriptionStatus,
} from 'src/models/organizations.models'
import { UserFeature } from 'src/models/user.models'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  useQueryOrganizationSaasTier,
  useQueryOrganizationSubscription,
} from 'src/queries/organizations/saas.queries'
import { getOrganization } from 'src/redux/actions/data/organizations.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectOrganizationIdFromRoute,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

const ManageSubscription: React.FC = () => {
  const translate = useTranslate()
  const user = useSelector(selectUserListModelFromState)
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const appName = useSelector(selectAppName)
  const { analytics } = useAnalyticsContext()

  const dispatch = useBackgroundDispatch()
  useEffect(() => {
    if (organizationId) {
      dispatch(getOrganization(organizationId, true))
    }
  }, [organizationId])

  const { data: currentPlan } = useQueryOrganizationSaasTier({
    orgId: organization?.id || '',
    enabled: !!organization?.id,
  })

  const { data: organizationSubscription } = useQueryOrganizationSubscription({
    orgId: organizationId,
  })

  const { data: userFeatures, isLoading: isFeaturesLoading } =
    useQueryUserFeatures([UserFeature.SAAS_ENABLED])

  // Track subscription status to detect cancellations made via Chargebee portal
  const prevStatusRef = useRef<string | undefined>(undefined)
  const userOpenedChargebee = useRef(false)
  const [showChurnSurveyModal, setShowChurnSurveyModal] = useState(false)
  const handleSurveySubmit = useChurnSurveySubmit(
    organizationId,
    'chargebee_portal',
  )

  const subscriptionStatus =
    organizationSubscription?.subscription?.state?.status

  useEffect(() => {
    if (subscriptionStatus === undefined) return

    if (prevStatusRef.current === undefined) {
      prevStatusRef.current = subscriptionStatus
      return
    }

    if (
      userOpenedChargebee.current &&
      prevStatusRef.current === SubscriptionStatus.ACTIVE &&
      (subscriptionStatus === SubscriptionStatus.NON_RENEWING ||
        subscriptionStatus === SubscriptionStatus.CANCELLED)
    ) {
      setShowChurnSurveyModal(true)
      analytics.track('churn survey', {
        action: 'viewed',
        source: 'chargebee_portal',
      })
    }

    prevStatusRef.current = subscriptionStatus
  }, [subscriptionStatus, analytics])

  const handleSurveyClose = useCallback((): void => {
    setShowChurnSurveyModal(false)
  }, [])

  // Show loading state while data is being fetched
  if (
    user === undefined ||
    organization === undefined ||
    currentPlan === undefined ||
    isFeaturesLoading
  ) {
    return <LoadingBackgroundPage />
  }

  // Show not-found page if SaaS is not enabled for this user
  if (!userFeatures?.SAAS_ENABLED || !user.hasOrgAdminPermissions) {
    return <NotFoundBackgroundPage />
  }

  // cancelsAt does not exist on all potential state types, thus the funky-ness here
  const cancelsAt = (
    organizationSubscription?.subscription?.state as { cancelsAt: string }
  )?.cancelsAt
  const subscriptionState = organizationSubscription?.subscription?.state
  const usageResetBillingAnchorDate =
    subscriptionState?.status === SubscriptionStatus.ACTIVE
      ? subscriptionState.nextBillingAt
      : subscriptionState?.status === SubscriptionStatus.NON_RENEWING
        ? subscriptionState.cancelsAt
        : subscriptionState?.status === SubscriptionStatus.IN_TRIAL
          ? subscriptionState.trialEnd
          : undefined

  return (
    <>
      <Helmet>
        <title>{`Organization Info | ${appName}`}</title>
        <meta
          name="description"
          content={`Organization Info page of ${appName}`}
        />
      </Helmet>
      <LayoutPage padding={'medium'}>
        <div className={'flex-column gap-12'}>
          <div className={'flex-column gap-6'}>
            {organizationSubscription?.subscription &&
              cancelsAt !== undefined && (
                <AlertBanner
                  data-testid={'manage-subscription-alert'}
                  variant={'warning'}
                  icon={<AlertTriangleIcon stroke={yellow800} />}
                  title={translate('Your ${tier} subscription ends ${date}.', {
                    tier: getSaasTierLabel(
                      organizationSubscription.subscription.tier,
                    ),
                    date: moment(cancelsAt).format('MMMM D, YYYY'),
                  })}
                />
              )}
            <ManageSubscriptionHeader
              currentTier={currentPlan.tier}
              organizationId={organizationId}
              onChargebeeOpened={() => {
                userOpenedChargebee.current = true
              }}
            />
          </div>
          <ManageSubscriptionUsageMeters
            organizationId={organizationId}
            currentTier={currentPlan.tier}
            billingAnchorDate={usageResetBillingAnchorDate}
            grandfathered={organizationSubscription?.subscription?.grandfathered}
          />
          <ManageSubscriptionPromo
            organizationId={organizationId}
            currentTier={currentPlan.tier}
          />
        </div>
      </LayoutPage>
      <BaseModal
        isOpen={showChurnSurveyModal}
        onClose={handleSurveyClose}
        showCloseButton
        aria-label={translate('Cancellation feedback survey')}
      >
        <ChurnSurveyScreen
          onSubmit={handleSurveySubmit}
          onSkip={handleSurveyClose}
        />
      </BaseModal>
    </>
  )
}

export default ManageSubscription
